import { useEffect, useState } from 'react';

import { addToRecentlyViewed } from 'Api/RecentlyViewed/utility';
import {
  fetchProduct,
  fetchProductPreview,
} from 'Api/Product/product-service';
import GAnalyticsRedux from 'Helpers/GAnalyticsRedux';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import ProductRequirements from 'Helpers/Product/ProductRequirements';

/*
  * Custom hook to fetch product data based on slug or ID.
  * Handles loading state, not found state, and redirects.
  * @param {Object} params - Parameters for fetching the product.
  * @param {string} params.slug - The slug of the product.
  * @param {string} params.id - The ID of the product preview.
  * @param {string} params.type - The type of the product.
  * @param {boolean} params.isPreview - Whether the product is a preview.
  * @returns {Object} - An object containing the product data, loading state, not
  * found state, redirect URL, and URL redirect.
*/
const useFetchProduct = ({ slug, id, type, isPreview }) => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [redirect, setRedirect] = useState(null);
  const [urlRedirect, setUrlRedirect] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true); // switch to loading state
      setNotFound(false);
      setRedirect(null);

      return fetchProduct(slug)
        .then((result) => {
          if (result.notFound) {
            setTimeout(() => {
              window.prerenderReady = true;
            }, 1500);
            return setNotFound(true);
          }

          if (result.redirect) {
            return setRedirect(result.redirect);
          }
          const foundProduct = result.product;


          const urlCorrection = ProductRequirements.WrongUrlCheck(
            foundProduct.type, foundProduct.slug, type, slug,
          );

          if (urlCorrection && urlCorrection.isWrongType && !isPreview) {
            setIsLoading(false);
            return setUrlRedirect(urlCorrection.modifiedUrl);
          }

          setProduct(foundProduct);
          setIsLoading(false);

          GAnalyticsRedux(foundProduct);
          addToRecentlyViewed(foundProduct);

          setTimeout(() => {
            window.prerenderReady = true;
          }, 1500);
          return null;
        }).catch(setFetchError);
    };

    const loadProductPreview = async () => {
      let encodedKey = '';
      const key = getUrlParameter('key');

      if (key) {
        encodedKey = encodeURIComponent(key);
      }

      const result = await fetchProductPreview(id, encodedKey);

      if (result.notFound) {
        return setNotFound(true);
      }

      setIsLoading(false);
      return setProduct(result.product);
    };

    if (slug) {
      loadProduct();
    } else if (id) {
      loadProductPreview();
    } else {
      setNotFound(true);
    }
  }, [slug, id, isPreview, type]);

  if (fetchError) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    throw fetchError;
  }

  return {
    product,
    isLoading,
    notFound,
    redirect,
    urlRedirect,
  };
};

export default useFetchProduct;
